import { Box, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import { AiSummaryIcon } from '@databases/icons/AiSummaryIcon';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import { DatabaseFieldType } from '@databases/types/database';
import React, { type FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';

import { addFieldMenuMessages } from './messages';
import type { AddFieldMenuSelection, FieldTypeEntry } from './types';

const itemStyles = xcss({
  width: '100%',
  borderRadius: 'radius.small',
  paddingInline: 'space.050',
  paddingBlock: 'space.075',
  color: 'color.text',
  backgroundColor: 'color.background.neutral.subtle',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered',
  },
  ':active': {
    backgroundColor: 'color.background.neutral.subtle.pressed',
  },
});

const iconWrapperStyles = xcss({
  display: 'inline-flex',
  alignItems: 'center',
});

export const FieldTypeMenuItem: FunctionComponent<{
  fieldType: FieldTypeEntry;
  onSelect?: (selection: AddFieldMenuSelection) => void;
}> = ({ fieldType, onSelect }) => {
  const Icon = fieldType.Icon;

  return (
    <Pressable
      xcss={itemStyles}
      onClick={() => onSelect?.({ type: 'field-type', fieldType: fieldType.type })}
      testId={`add-field-menu-${fieldType.type}`}
    >
      <Inline space="space.100" alignBlock="center" shouldWrap={false}>
        <Box xcss={iconWrapperStyles}>
          {Icon ? (
            <Icon label="" />
          ) : (
            <FieldTypeIcon
              type={fieldType.type}
              label={fieldType.labelDescriptor}
              noTooltip
            />
          )}
        </Box>
        <Text>{fieldType.label}</Text>
      </Inline>
    </Pressable>
  );
};

export const AiSummarizeMenuItem: FunctionComponent<{
  onSelect?: (selection: AddFieldMenuSelection) => void;
}> = ({ onSelect }) => {
  const { formatMessage } = useIntl();

  return (
    <Pressable
      xcss={itemStyles}
      onClick={() =>
        onSelect?.({
          type: 'ai-summarize',
          fieldType: DatabaseFieldType.AI_SUMMARY,
        })
      }
      testId="add-field-menu-ai-summary"
    >
      <Inline space="space.100" alignBlock="center" shouldWrap={false}>
        <Box xcss={iconWrapperStyles}>
          <AiSummaryIcon label="" />
        </Box>
        <Text>{formatMessage(addFieldMenuMessages.aiSummary)}</Text>
      </Inline>
    </Pressable>
  );
};
